import type { ActionContext } from 'vuex';
import apiClient from '../../api';
import { isObj, isArr } from '../../utils';
import store from '..';
import initialState from '../initialState';
import type { LabelListItem, PaginationMeta } from '@/types';
import type { LabelsQueryParams, LabelPayload } from '@/api/merchy/labels';

const labelsApi = apiClient.merchy.labels;

export interface LabelsState {
  /**
   * Populated either from a listing row (via setActiveItem) or from the detail
   * endpoint (via getItem/update), so only the fields common to both are typed.
   */
  activeItem: Partial<LabelListItem>
  items: LabelListItem[]
  itemsMeta: Partial<PaginationMeta>
}

type LabelsActionContext = ActionContext<LabelsState, unknown>;

/**
 * @description Is valid
 * @param labels
 * @returns {boolean}
 */
const isValid = (labels: LabelsState) => {
  const { activeItem, items, itemsMeta } = labels;

  return (
    isObj(labels) &&
    isObj(activeItem) &&
    isArr(items) &&
    isObj(itemsMeta)
  );
};

/**
 * @description Init state
 * @param initialState
 * @returns {*}
 */
const initState = (initialState: LabelsState): LabelsState => {
  if (!isValid(initialState)) {
    throw Error('Invalid initial labels state');
  }

  const { activeItem, items, itemsMeta } =
    initialState;
  return {
    activeItem,
    items,
    itemsMeta,
  };
};

/**
 * @description Getters
 * @type {*}
 */
export const getters = {
  activeItem: ({ activeItem }: LabelsState) => activeItem,
  items: ({ items }: LabelsState) => items,
  itemsMeta: ({ itemsMeta }: LabelsState) => itemsMeta,
  itemsData: ({ items, itemsMeta }: LabelsState) => ({ items, itemsMeta }),
};

/**
 * @description Handle alerts
 * @param message
 * @param alertType
 */
const handleAlerts = (message: string, alertType = 'success') => {
  const alert = {
    data: {
      message,
    },
  };

  return store.dispatch('alerts/set', {
    data: alert,
    alertType,
  });
};

const actions = {
  create: (context: LabelsActionContext, label: LabelPayload) =>
    labelsApi
      .createItem(label)
      .then(() => handleAlerts('Label created successfully.')),
  update: ({ commit, state }: LabelsActionContext, comment: LabelPayload) =>
    labelsApi.updateItem(comment).then((res) => {
      const { activeItem } = state;
      const nextLabel = res.data.data;

      const nextState = {
        ...state,
        activeItem: {
          ...activeItem,
          ...nextLabel,
        },
      };

      commit('SET', nextState);
      return handleAlerts('Label updated successfully.');
    }),
  remove: (context: LabelsActionContext, label: { id: number }) =>
    labelsApi
      .removeItem(label)
      .then(() => handleAlerts('Label removed successfully.')),
  getItem: ({ commit, state }: LabelsActionContext, query: { id: number }) =>
    labelsApi.getItem(query).then((res) => {
      const { activeItem } = state;

      const nextState = {
        ...state,
        activeItem: {
          ...activeItem,
          ...res.data.data,
        },
      };

      return commit('SET', nextState);
    }),
  /**
   * Fetches specific labels by id and returns them, without committing: the
   * cached `items` listing belongs to whatever the user last browsed, and a
   * lookup for a couple of ids must not replace it.
   */
  getItemsByIds: (_context: LabelsActionContext, { ids }: { ids: number[] }) =>
    labelsApi
      .getItems({ ids, per_page: ids.length })
      .then((res) => res.data.data),
  getItems: ({ commit, state }: LabelsActionContext, query: LabelsQueryParams) => {
    const labelsQuery = {
      ...query,
      // is_system_label: 0,
    };

    return labelsApi.getItems(labelsQuery).then((res) => {
      const { data, meta } = res.data;
      const nextItems = {
        items: data,
        itemsMeta: meta,
      };

      const nextState = {
        ...state,
        ...nextItems,
      };

      commit('SET', nextState);
      return nextItems;
    });
  },
  setActiveItem: ({ commit, state }: LabelsActionContext, nextActiveItem: Partial<LabelListItem>) => {
    const nextState = {
      ...state,
      activeItem: {
        ...state.activeItem,
        ...nextActiveItem,
      },
    };

    return commit('SET', nextState);
  },
  reset: ({ commit }: LabelsActionContext) => commit('SET', initialState.labels),
  set: ({ commit }: LabelsActionContext, labels: LabelsState) => {
    commit('SET', labels);
  },
};

const mutations = {
  SET(state: LabelsState, labels: LabelsState) {
    /* eslint-disable no-param-reassign */
    state.activeItem = labels.activeItem;
    state.items = labels.items;
    state.itemsMeta = labels.itemsMeta;
  },
};

export default (initialState: LabelsState) => ({
  namespaced: true,
  state: initState(initialState),
  getters,
  actions,
  mutations,
});
